class FreeGiftBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.amountToSpendShippingElement = this.querySelector('amount-to-spend-shipping');
    this.amountLeftShippingElement = this.querySelector('amount-left-shipping');
    this.shippingMoneyValueElement = this.amountLeftShippingElement?.querySelector('money-value');
    this.shippingReachedElement = this.querySelector('shipping-reached');
    this.amountToSpendElement = this.querySelector('amount-to-spend-gift');
    this.amountLeftElement = this.querySelector('amount-left-gift');
    this.giftMoneyValueElement = this.amountLeftElement?.querySelector('money-value');
    this.giftReachedElement = this.querySelector('gift-reached');
    this.shippingIcon = this.querySelector('.free-gift-bar__icon--shipping');
    this.giftIcon = this.querySelector('.free-gift-bar__icon--gift');
    this.track = this.querySelector('free-gift-bar-track');
    this.shippingMilestone = this.querySelector('.free-gift-bar__milestone--shipping');
    this.giftMilestone = this.querySelector('.free-gift-bar__milestone--gift');

    this.shippingThreshold = Number(this.getAttribute('shipping-threshold'));
    this.giftThreshold = Number(this.getAttribute('gift-threshold'));
    this.freeGiftProductId = this.getAttribute('free-gift-product-id');
    this.value = Number(this.getAttribute('value'));
    this.previousValue = this.value;

    this._setVariables();

    this.unsubscribe = window.theme.cart.store.subscribe((state) => {
      if (this.value !== state.products.total_price) {
        this.previousValue = this.value;
        this.value = state.products.total_price;
        this._update();
      }

      if (
        this.value >= this.giftThreshold &&
        this.classList.contains('is-visible')
      ) {
        this.track.addEventListener(
          'transitionend',
          () => {
            setTimeout(() => {
              this._playGoalAnimation();
            }, 250);
          },
          {
            once: true,
          },
        );
      }
    });

    this.revealDelay = this.getAttribute('reveal-delay') || 0;

    setTimeout(() => {
      const goalAnimationElement = this.querySelector(
        'free-gift-bar-goal-animation',
      );

      if (goalAnimationElement && window.lottie && !this.goalAnimation) {
        this.goalAnimation = lottie.loadAnimation({
          container: goalAnimationElement,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          useWebWorker: false,
          path: goalAnimationElement.getAttribute('data-url'),
        });
      }
    });

    let options = {
      rootMargin: '0px',
      threshold: 1.0,
    };

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setTimeout(() => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');

            setTimeout(() => {
              if (this.amountLeftGift === 0) this._playGoalAnimation();
            }, 1000);

            this.visibilityObserver.disconnect();
          }
        }, this.revealDelay);
      });
    }, options);

    this.visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.classList.remove('is-visible');
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
  }

  _setVariables() {
    // Calculate amounts left for both thresholds
    this.amountLeftShipping =
      this.shippingThreshold - this.value <= 0 ? 0 : this.shippingThreshold - this.value;
    this.amountLeftGift =
      this.giftThreshold - this.value <= 0 ? 0 : this.giftThreshold - this.value;

    // Format money values
    this.moneyLeftShipping = window.Shopify.formatMoney(
      this.amountLeftShipping,
      window.theme.money_format,
    ).replace(/[\.,]00$/, '');

    this.moneyLeftGift = window.Shopify.formatMoney(
      this.amountLeftGift,
      window.theme.money_format,
    ).replace(/[\.,]00$/, '');

    // Calculate progress towards gift (main goal)
    this.displacementGift = Math.round((this.amountLeftGift / this.giftThreshold) * 100);
  }

  _update() {
    this.setAttribute('value', this.value);
    this._setVariables();

    // Check if gift threshold was just reached or crossed
    const wasBelowThreshold = this.previousValue < this.giftThreshold;
    const isNowAtThreshold = this.value >= this.giftThreshold;
    const wasAboveThreshold = this.previousValue >= this.giftThreshold;
    const isNowBelowThreshold = this.value < this.giftThreshold;

    if (wasBelowThreshold && isNowAtThreshold) {
      this._addFreeGiftToCart();
    } else if (wasAboveThreshold && isNowBelowThreshold) {
      this._removeFreeGiftFromCart();
    }

    // Determine which message to show based on cart value
    const showShippingMessage = this.value < this.shippingThreshold;
    const showGiftMessage = this.value >= this.shippingThreshold;

    // Update money value elements
    if (this.shippingMoneyValueElement) {
      this.shippingMoneyValueElement.innerHTML = this.moneyLeftShipping;
    }
    if (this.giftMoneyValueElement) {
      this.giftMoneyValueElement.innerHTML = this.moneyLeftGift;
    }

    // Show/hide icons based on active message
    if (showShippingMessage) {
      if (this.shippingIcon) {
        this.shippingIcon.removeAttribute('hidden');
      }
      if (this.giftIcon) {
        this.giftIcon.setAttribute('hidden', '');
      }
    } else {
      if (this.shippingIcon) {
        this.shippingIcon.setAttribute('hidden', '');
      }
      if (this.giftIcon) {
        this.giftIcon.removeAttribute('hidden');
      }
    }

    // Show/hide shipping elements
    if (showShippingMessage) {
      if (this.value === 0) {
        this.amountToSpendShippingElement.removeAttribute('hidden');
      } else {
        this.amountToSpendShippingElement.setAttribute('hidden', '');
      }

      if (this.value === 0 || this.amountLeftShipping === 0) {
        this.amountLeftShippingElement.setAttribute('hidden', '');
      } else {
        this.amountLeftShippingElement.removeAttribute('hidden');
      }

      if (this.amountLeftShipping > 0) {
        this.shippingReachedElement.setAttribute('hidden', '');
      } else {
        this.shippingReachedElement.removeAttribute('hidden');
      }
    } else {
      this.amountToSpendShippingElement.setAttribute('hidden', '');
      this.amountLeftShippingElement.setAttribute('hidden', '');
      this.shippingReachedElement.setAttribute('hidden', '');
    }

    // Show/hide gift elements
    if (showGiftMessage) {
      if (this.value === 0) {
        this.amountToSpendElement.removeAttribute('hidden');
      } else {
        this.amountToSpendElement.setAttribute('hidden', '');
      }

      if (this.value === 0 || this.amountLeftGift === 0) {
        this.amountLeftElement.setAttribute('hidden', '');
      } else {
        this.amountLeftElement.removeAttribute('hidden');
      }

      if (this.amountLeftGift > 0) {
        this.giftReachedElement.setAttribute('hidden', '');
      } else {
        this.giftReachedElement.removeAttribute('hidden');
      }
    } else {
      this.amountToSpendElement.setAttribute('hidden', '');
      this.amountLeftElement.setAttribute('hidden', '');
      this.giftReachedElement.setAttribute('hidden', '');
    }

    // Update progress bar displacement based on gift threshold (always the main goal)
    const progressTowardsGift = Math.round(((this.giftThreshold - this.value) / this.giftThreshold) * 100);
    const clampedProgress = Math.max(0, Math.min(100, progressTowardsGift));
    this.track.style.setProperty('--displacement', `${clampedProgress}%`);

    // Update milestone states
    this._updateMilestoneStates();
  }

  _updateMilestoneStates() {
    // Update shipping milestone state
    if (this.shippingMilestone) {
      if (this.value >= this.shippingThreshold) {
        this.shippingMilestone.classList.add('is-reached');
      } else {
        this.shippingMilestone.classList.remove('is-reached');
      }
    }

    // Update gift milestone state
    if (this.giftMilestone) {
      if (this.value >= this.giftThreshold) {
        this.giftMilestone.classList.add('is-reached');
      } else {
        this.giftMilestone.classList.remove('is-reached');
      }
    }
  }

  _playGoalAnimation() {
    const animationShown = Boolean(
      localStorage.getItem('freeGiftAnimationShown'),
    );

    if (animationShown) return;

    if (this.goalAnimation) {
      localStorage.setItem('freeGiftAnimationShown', true);
      this.goalAnimation.play();
    }
  }

  async _addFreeGiftToCart() {
    // Get the free gift product ID from attribute
    if (!this.freeGiftProductId) {
      return;
    }

    if (!this.freeGiftProductId || this.freeGiftProductId === '') {
      return;
    }

    try {
      // Use variant ID from theme global variable
      const variantId = window.theme?.free_gift?.variant_id || this.freeGiftProductId;

      await window.theme.cart.store.getState().add({
        id: variantId,
        quantity: 1
      });

      // Check if the gift was actually added
      setTimeout(() => {
        const cartState = window.theme.cart.store.getState();
        const freeGiftInCart = cartState.products.items.find(item =>
          item.product_id === Number(this.freeGiftProductId) ||
          item.id === Number(this.freeGiftProductId) ||
          item.variant_id === Number(this.freeGiftProductId)
        );

        if (freeGiftInCart) {
          this._playGoalAnimation();
        }
      }, 1000);

    } catch (error) {
      // Silent fail
    }
  }

  async _tryDirectAdd() {
    try {
      await window.theme.cart.store.getState().add({
        id: this.freeGiftProductId,
        quantity: 1
      });


    } catch (error) {
      // Silent fail
    }
  }

  async _removeFreeGiftFromCart() {
    // Get the free gift product ID from attribute
    if (!this.freeGiftProductId) {
      return;
    }

    try {
      // Find the free gift item in cart
      const cartState = window.theme.cart.store.getState();
      const freeGiftItem = cartState.products.items.find(item =>
        item.product_id === Number(this.freeGiftProductId)
      );

      if (freeGiftItem) {
        // Remove free gift from cart
        await window.theme.cart.store.getState().change(freeGiftItem.key, 0);
      }

    } catch (error) {
      // Silent fail
    }
  }
}

customElements.define('free-gift-bar', FreeGiftBar);
